import { useMutation } from '@tanstack/react-query'
import { Building2, MapPin, Search } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { GeoPoint } from '@aeris/contracts'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  InlineAlert,
  Input,
  Spinner,
} from '@aeris/ui'

import {
  EMPTY_ADDRESS,
  ufOrNull,
  type AddressValues,
} from '../lib/address-values.ts'
import {
  ApiError,
  geocodeAddress,
  locateCustomer,
  searchPlaces,
  type UnlocatedCustomerView,
} from '../lib/api.ts'
import { lookupCnpj } from '../lib/cnpj.ts'
import { shopNameOf } from '../lib/shop-name.ts'

import { AddressInputs } from './address-inputs.tsx'
import { MapView } from './map-view.tsx'

/**
 * Localizar um cliente no mapa (F265).
 *
 * O lojista de GV quase nunca tem endereço no cadastro — o motoboy sabe o
 * caminho de cabeça. Três jeitos de achar, e a pessoa sempre confere o pino:
 *
 * 1. **pelo nome da loja** — "Imperio Cell", puxado para perto da saída;
 * 2. **pelo CNPJ** — o endereço da Receita, depois achado no mapa;
 * 3. **pelo endereço que ele já tem**, quando tem.
 *
 * Pino que a pessoa arrastou grava como `manual`; o sugerido e aceito, como
 * `google`. Nada é gravado sem alguém ter olhado o mapa.
 */

type Candidate = {
  readonly key: string
  readonly title: string
  readonly formattedAddress: string
  readonly point: GeoPoint
  /** Só do Geocoding: o quanto o ponto é confiável. */
  readonly precision: 'exact' | 'interpolated' | 'street' | 'area' | null
  readonly parts: {
    street: string | null
    number: string | null
    district: string | null
    city: string | null
    uf: string | null
    cep: string | null
  }
}

const PRECISION_LABELS: Record<NonNullable<Candidate['precision']>, string> = {
  exact: 'no número',
  interpolated: 'perto do número',
  street: 'só a rua — arraste até a loja',
  area: 'só a região — arraste até a loja',
}

const addressFromParts = (
  parts: Candidate['parts'],
  current: AddressValues
): AddressValues => ({
  ...current,
  kind: 'delivery',
  cep: parts.cep ?? current.cep,
  street: parts.street ?? current.street,
  number: parts.number ?? current.number,
  district: parts.district ?? current.district,
  city: parts.city ?? current.city,
  uf: ufOrNull(parts.uf ?? '') ?? current.uf,
})

const errorText = (cause: unknown): string =>
  cause instanceof ApiError ? cause.message : 'Não foi possível agora.'

export const LocateCustomerDialog = ({
  customer,
  open,
  onOpenChange,
  onLocated,
}: {
  customer: UnlocatedCustomerView
  open: boolean
  onOpenChange: (open: boolean) => void
  onLocated: () => void
}): ReactNode => {
  const [query, setQuery] = useState(() => shopNameOf(customer))
  const [candidates, setCandidates] = useState<Candidate[] | null>(null)
  const [chosen, setChosen] = useState<Candidate | null>(null)
  /** O endereço que ele já tem e que está sendo localizado. */
  const [addressId, setAddressId] = useState<string | null>(null)
  const [pin, setPin] = useState<GeoPoint | null>(null)
  const [moved, setMoved] = useState(false)
  const [address, setAddress] = useState<AddressValues>({
    ...EMPTY_ADDRESS,
    kind: 'delivery',
    uf: 'MG',
  })
  const [error, setError] = useState<string | null>(null)

  const choose = (candidate: Candidate, existing: string | null): void => {
    setChosen(candidate)
    setPin(candidate.point)
    setMoved(false)
    setAddressId(existing)
    if (existing === null)
      setAddress((current) => addressFromParts(candidate.parts, current))
  }

  const search = useMutation({
    mutationFn: async (
      how:
        | { kind: 'name' }
        | { kind: 'cnpj' }
        | { kind: 'address'; addressId: string }
    ): Promise<{ list: Candidate[]; addressId: string | null }> => {
      if (how.kind === 'name') {
        const found = await searchPlaces(query)
        return {
          addressId: null,
          list: found.map((place, index) => ({
            key: `place-${index}`,
            title: place.name,
            formattedAddress: place.formattedAddress,
            point: { latitude: place.latitude, longitude: place.longitude },
            precision: null,
            parts: place.parts,
          })),
        }
      }
      const source =
        how.kind === 'cnpj'
          ? ((await lookupCnpj(customer.document ?? ''))?.address ?? null)
          : (customer.addresses.find((entry) => entry.id === how.addressId) ??
            null)
      if (source === null) {
        throw new Error('A Receita não devolveu endereço para este CNPJ.')
      }
      const uf = ufOrNull(source.uf)
      if (
        uf === null ||
        source.street.trim() === '' ||
        source.city.trim() === ''
      ) {
        throw new Error('O endereço está incompleto para achar no mapa.')
      }
      const found = await geocodeAddress({
        street: source.street,
        number: source.number,
        district: source.district,
        city: source.city,
        uf,
        cep: source.cep,
      })
      return {
        addressId: how.kind === 'address' ? how.addressId : null,
        list: found.map((place, index) => ({
          key: `geo-${index}`,
          title: place.formattedAddress,
          formattedAddress: place.formattedAddress,
          point: { latitude: place.latitude, longitude: place.longitude },
          precision: place.precision,
          // Da Receita, vale o que a Receita disse; o Google só põe o ponto.
          parts:
            how.kind === 'cnpj'
              ? {
                  street: source.street,
                  number: source.number,
                  district: source.district,
                  city: source.city,
                  uf: source.uf,
                  cep: source.cep,
                }
              : place.parts,
        })),
      }
    },
    onMutate: () => {
      setError(null)
      setChosen(null)
      setPin(null)
    },
    onSuccess: ({ list, addressId: existing }) => {
      setCandidates(list)
      const first = list[0]
      if (first !== undefined) choose(first, existing)
    },
    onError: (cause: unknown) => {
      setCandidates(null)
      setError(
        cause instanceof Error && !(cause instanceof ApiError)
          ? cause.message
          : errorText(cause)
      )
    },
  })

  const save = useMutation({
    mutationFn: async () => {
      if (pin === null) throw new Error('Escolha um ponto no mapa.')
      const source = moved ? 'manual' : 'google'
      if (addressId !== null) {
        return await locateCustomer(customer.partyId, {
          addressId,
          point: pin,
          source,
        })
      }
      const uf = ufOrNull(address.uf)
      if (
        address.street.trim() === '' ||
        address.city.trim() === '' ||
        uf === null
      ) {
        throw new Error('Preencha rua e cidade do endereço de entrega.')
      }
      const blank = (value: string): string | null =>
        value.trim() === '' ? null : value.trim()
      return await locateCustomer(customer.partyId, {
        address: {
          cep: blank(address.cep),
          street: address.street.trim(),
          number: blank(address.number),
          complement: blank(address.complement),
          district: blank(address.district),
          city: address.city.trim(),
          uf,
        },
        point: pin,
        source,
      })
    },
    onSuccess: () => {
      onLocated()
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof Error && !(cause instanceof ApiError)
          ? cause.message
          : errorText(cause)
      )
    },
  })

  const isCompany = (customer.document ?? '').replace(/\D/g, '').length === 14
  const busy = search.isPending || save.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Localizar ${shopNameOf(customer)}`}
        description={
          customer.deliveries === 0
            ? customer.legalName
            : `${customer.legalName} · ${customer.deliveries} ${customer.deliveries === 1 ? 'entrega' : 'entregas'} nos últimos 60 dias`
        }
        className="max-w-3xl"
      >
        <div className="flex flex-col gap-4">
          {customer.addresses.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-(length:--text-sm) font-medium">
                Endereço que ele já tem
              </p>
              {customer.addresses.map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2"
                >
                  <span className="text-(length:--text-sm)">
                    {[entry.street, entry.number].filter(Boolean).join(', ')}
                    {entry.district === null
                      ? ''
                      : ` — ${entry.district}`}, {entry.city}/{entry.uf}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      search.mutate({ kind: 'address', addressId: entry.id })
                    }}
                  >
                    <MapPin aria-hidden="true" className="h-4 w-4" />
                    Achar no mapa
                  </Button>
                </div>
              ))}
            </div>
          ) : null}

          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              if (query.trim().length >= 3) search.mutate({ kind: 'name' })
            }}
          >
            <label className="flex min-w-60 flex-1 flex-col gap-1 text-(length:--text-sm)">
              Nome da loja
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                }}
                placeholder="Imperio Cell"
              />
            </label>
            <Button type="submit" disabled={busy || query.trim().length < 3}>
              <Search aria-hidden="true" className="h-4 w-4" />
              Buscar pelo nome
            </Button>
            {isCompany ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  search.mutate({ kind: 'cnpj' })
                }}
              >
                <Building2 aria-hidden="true" className="h-4 w-4" />
                Buscar pelo CNPJ
              </Button>
            ) : null}
          </form>

          {search.isPending ? <Spinner label="Buscando no mapa…" /> : null}

          {candidates !== null && candidates.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Não achei. Tente outro nome, o CNPJ, ou digite o endereço e
              arraste o pino.
            </p>
          ) : null}

          {candidates !== null && candidates.length > 1 ? (
            <ul aria-label="O que o mapa achou" className="flex flex-col gap-1">
              {candidates.map((candidate) => (
                <li key={candidate.key}>
                  <button
                    type="button"
                    aria-pressed={chosen?.key === candidate.key}
                    onClick={() => {
                      choose(candidate, addressId)
                    }}
                    className="w-full rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-left text-(length:--text-sm) aria-pressed:border-(--accent) aria-pressed:bg-(--surface-sunken)"
                  >
                    <span className="font-medium">{candidate.title}</span>
                    {candidate.title === candidate.formattedAddress ? null : (
                      <span className="block text-(--text-muted)">
                        {candidate.formattedAddress}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {chosen !== null && pin !== null ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2 text-(length:--text-sm)">
                <span>{chosen.formattedAddress}</span>
                {chosen.precision === null ? null : (
                  <Badge
                    tone={
                      chosen.precision === 'exact' ||
                      chosen.precision === 'interpolated'
                        ? 'success'
                        : 'warning'
                    }
                  >
                    {PRECISION_LABELS[chosen.precision]}
                  </Badge>
                )}
                {moved ? <Badge tone="info">pino ajustado à mão</Badge> : null}
              </div>
              <MapView
                label={`O mapa de ${shopNameOf(customer)}`}
                center={pin}
                zoom={17}
                pin={{
                  point: pin,
                  onMove: (point) => {
                    setPin(point)
                    setMoved(true)
                  },
                }}
              />
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Confira se o pino está na porta da loja. Arraste se não estiver
                (ou use as setas com o pino selecionado).
              </p>
              {addressId === null ? (
                <div className="flex flex-col gap-2">
                  <p className="text-(length:--text-sm) font-medium">
                    O endereço de entrega que vai para o cadastro
                  </p>
                  <AddressInputs values={address} onChange={setAddress} />
                </div>
              ) : null}
            </div>
          ) : null}

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancelar
          </Button>
          <Button
            disabled={pin === null || busy}
            onClick={() => {
              save.mutate()
            }}
          >
            {save.isPending ? 'Salvando…' : 'Salvar o ponto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
